import { FileCardLocal, type FileCardLocalProps } from "@msuite/ui/file-upload";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { retryLimit, useQuery } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { createFile } from "@msuite/utils/files";
import type { Nullable, Tables } from "@msuite/utils/types";
import { Fragment, useEffect, useState } from "react";
import { client } from "@/hono";

/** Props Interface */
interface FileCardRemoteProps {
	fileId: Tables<"files">["id"];
	onInteraction: (() => Promise<void>) | null;
	onUnlink?: () => Promise<void>;
	withRemove?: boolean;
	/**
	 * Whether the caller may change what hangs on the file's parent — the answer of
	 * `useFileCapabilities(parent).canWrite`. Unlinking and deleting both follow the parent's
	 * `update`, so this is what decides whether the card offers either.
	 *
	 * Defaults to `true` because most cards are rendered inside the parent's own edit form, and
	 * that form is the parent's edit surface: it is reachable only under the parent's `update`, so
	 * asking again would answer what the form's caller already answered. A card shown anywhere
	 * else — beside a read view, in a list — passes the parent-derived value.
	 */
	canRemove?: boolean;
	/**
	 * Whether a card that unlinks also offers to delete the file itself. False where the file
	 * belongs to something else as well — a delivery note's PDF is its email's attachment.
	 */
	withDelete?: boolean;
	variant?: FileCardLocalProps["variant"];
	title?: FileCardLocalProps["title"];
	caption?: FileCardLocalProps["caption"];
}

export function FileCardRemote({
	fileId,
	onInteraction,
	onUnlink,
	withRemove = true,
	canRemove = true,
	withDelete = true,
	variant,
	title,
	caption,
}: FileCardRemoteProps) {
	/** State */
	const [fetchedFile, setFetchedFile] = useState<Nullable<File>>(null);
	const [isDeleted, setIsDeleted] = useState(false);

	/** Hooks */
	const { t } = useTranslation();
	const deleteFileAsyncModal = useAsyncModal();
	const unlinkFileAsyncModal = useAsyncModal();

	/** Query */
	const { data } = useQuery({
		queryKey: ["file", fileId],
		queryFn: async () => {
			try {
				const response = await client.files[":fileId"].$get({
					param: {
						fileId,
					},
				});
				const data = await responseHandler(response);
				if (!data.signedUrl) throw new Error("Signed URL not found");
				if (!data.file) throw new Error("File not found");
				return data;
			} catch (error) {
				toast.error("Error fetching file. Please try again later.");
				console.error("Error fetching file:", error);
				return null;
			}
		},
		retry: retryLimit(5),
		staleTime: 1000 * 60 * 10,
		retryDelay: (attempt) => {
			return Math.min(1000 * 2 ** attempt, 30_000);
		},
	});

	/** Effects */
	useEffect(() => {
		if (!data) return;
		if (fetchedFile) return;
		async function handleOnCreateFile() {
			try {
				if (!data?.signedUrl) return;
				if (!data?.file) return;
				const fileObject = await createFile(
					data?.signedUrl,
					data?.file?.fileName,
					data?.file?.contentType,
				);
				if (!fileObject) return;
				setFetchedFile(fileObject);
			} catch (error) {
				toast.error("Error creating file. Please try again later.");
				console.error("Error creating file:", error);
			}
		}

		handleOnCreateFile();
	}, [data?.signedUrl, data?.file?.fileName, data?.file?.contentType]);

	/** Functions */
	async function handleOnDelete() {
		try {
			const confirmDeleteFile = await deleteFileAsyncModal.confirm();
			if (!confirmDeleteFile) throw new Error("User cancelled delete");
			const response = await client.files[":fileId"].$delete({
				param: {
					fileId,
				},
			});
			const success = (await responseHandler(response)).success;
			if (!success) throw new Error("Error deleting file");
			onInteraction?.();
			setIsDeleted(true);
		} catch (error) {
			console.error("Error deleting file:", error);
		}
	}

	async function handleOnUnlink() {
		try {
			if (!onUnlink) return;
			const confirmUnlinkFile = await unlinkFileAsyncModal.confirm();
			if (!confirmUnlinkFile) throw new Error("User cancelled unlink");
			await onUnlink();
			onInteraction?.();
			setIsDeleted(true);
		} catch (error) {
			console.error("Error unlinking file:", error);
		}
	}

	/** Computed */
	// Until the row is loaded there is nothing to remove, and the card shows neither control.
	const mayRemove = !!data?.file && canRemove;

	if (isDeleted) return true;

	/** Render */
	return (
		<Fragment>
			<FileCardLocal
				file={fetchedFile}
				removeType={onUnlink ? "unlink" : "delete"}
				withDownload
				withRemove={withRemove && mayRemove}
				withDelete={withDelete && !!onUnlink && mayRemove}
				onRemove={onUnlink ? handleOnUnlink : handleOnDelete}
				onDelete={onUnlink ? handleOnDelete : undefined}
				width={data?.file?.width}
				height={data?.file?.height}
				variant={variant}
				title={title}
				caption={caption}
			/>
			{unlinkFileAsyncModal.isOpen && (
				<ConfirmModal
					controller={unlinkFileAsyncModal}
					message={t("file_upload.unlink_file_confirm_message")}
				/>
			)}
			{deleteFileAsyncModal.isOpen && (
				<ConfirmModal
					controller={deleteFileAsyncModal}
					message={t("file_upload.delete_file_confirm_message")}
				/>
			)}
		</Fragment>
	);
}
